import { useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { authedUrl, API } from "@/lib/api";
import { viewHeaders } from "@/lib/viewTenant";

/** docOrInvoice: {kind: 'document'|'invoice', id, filename, content_type} */
export default function DocViewer({ open, onClose, item }) {
  const { getToken } = useAuth();
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ctype, setCtype] = useState("");

  useEffect(() => {
    if (!open || !item) return;
    let revoke = null;
    (async () => {
      setLoading(true);
      try {
        const token = await getToken();
        const url =
          item.kind === "invoice"
            ? `${API}/invoices/${item.id}/pdf?inline=1`
            : `${API}/documents/${item.id}/file?inline=1`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}`, ...viewHeaders() } });
        const b = await res.blob();
        const bu = URL.createObjectURL(b);
        setBlobUrl(bu);
        setCtype(b.type || item.content_type || "");
        revoke = () => URL.revokeObjectURL(bu);
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      if (revoke) revoke();
      setBlobUrl(null);
    };
    // eslint-disable-next-line
  }, [open, item]);

  const download = async () => {
    const path =
      item.kind === "invoice"
        ? `/invoices/${item.id}/pdf?inline=0`
        : `/documents/${item.id}/file?inline=0`;
    window.open(await authedUrl(path, getToken), "_blank");
  };

  const isImage = (ctype || item?.content_type || "").startsWith("image/");

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden">
        <DialogHeader className="px-5 py-3 border-b border-slate-200 flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-base truncate pr-4">{item?.filename || "Preview"}</DialogTitle>
          <Button size="sm" variant="outline" onClick={download} className="mr-8" data-testid="doc-download-btn">
            <Download className="w-4 h-4 mr-1" /> Download
          </Button>
        </DialogHeader>
        <div className="h-[75vh] bg-slate-50">
          {loading && <div className="h-full grid place-items-center text-slate-500">Loading…</div>}
          {!loading && blobUrl && isImage && (
            <img src={blobUrl} alt="" className="w-full h-full object-contain" />
          )}
          {!loading && blobUrl && !isImage && (
            <iframe src={blobUrl} title="doc" className="w-full h-full border-0" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
